import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  Select,
  Link,
  Text,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';

const SUPPORT_EMAIL = 'support@healfaster.ca';

const GENDER_OPTIONS = [
  { label: 'Select Gender', value: '' },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Other', value: 'other' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const PROVINCE_OPTIONS = [
  { label: 'Select Province', value: '' },
  { label: 'Alberta', value: 'AB' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland and Labrador', value: 'NL' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'Nunavut', value: 'NU' },
  { label: 'Ontario', value: 'ON' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Yukon', value: 'YT' },
];

export default function ClinicAddPatient() {
  const navigate = useNavigate();
  const base = useClinicBasePath();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [referredByDoctorId, setReferredByDoctorId] = useState('');
  const [status, setStatus] = useState('active');

  const { data: doctors = [] } = useQuery({
    queryKey: ['clinic-doctors'],
    queryFn: () => clinicPortalApi.getDoctors(),
  });

  const doctorOptions = [
    { label: 'Select Doctor', value: '' },
    ...doctors.map((d) => ({
      label: `Dr. ${d.firstName} ${d.lastName}`,
      value: d.id,
    })),
  ];

  const mutation = useMutation({
    mutationFn: (data: Record<string, any>) =>
      clinicPortalApi.createPatient(data as any),
    onSuccess: () => {
      navigate(`${base}/patients`);
    },
  });

  const handleSubmit = useCallback(() => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !gender || !streetAddress.trim()) return;
    mutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || undefined,
      phone: phone.trim(),
      age: age ? parseInt(age, 10) : undefined,
      gender: gender || undefined,
      dateOfBirth: dateOfBirth || undefined,
      streetAddress: streetAddress.trim(),
      city: city.trim() || undefined,
      province: province || undefined,
      postalCode: postalCode.trim() || undefined,
      referredByDoctorId: referredByDoctorId || undefined,
      status,
    });
  }, [firstName, lastName, email, phone, age, gender, dateOfBirth, streetAddress, city, province, postalCode, referredByDoctorId, status, mutation]);

  const isValid = firstName.trim() && lastName.trim() && phone.trim() && gender && streetAddress.trim();

  return (
    <Page
      title="Create Patient"
      backAction={{ content: 'Patients', onAction: () => navigate(`${base}/patients`) }}
    >
      <Card>
        <BlockStack gap="400">
          {mutation.isError && (() => {
            const errBody = (mutation.error as any)?.response?.data;
            const code = errBody?.message?.code || errBody?.code;
            if (code === 'PATIENT_AT_OTHER_CLINIC') {
              const otherClinic = errBody?.message?.existingClinicName || errBody?.existingClinicName;
              const subject = encodeURIComponent(
                `Patient transfer request${email ? ` — ${email}` : ''}`,
              );
              const body = encodeURIComponent(
                `Hi HealFaster team,\n\nWe'd like to transfer this patient to our clinic:\n\n` +
                  `Name: ${firstName} ${lastName}\nEmail: ${email}\n` +
                  (otherClinic ? `Currently registered with: ${otherClinic}\n` : '') +
                  `\nThanks.`,
              );
              return (
                <Banner tone="warning" title="Patient already at another clinic">
                  <BlockStack gap="200">
                    <Text as="p">
                      A patient with email <strong>{email}</strong> is already
                      active at{' '}
                      {otherClinic ? <strong>{otherClinic}</strong> : 'another clinic'}.
                      Only one clinic can manage a patient at a time.
                    </Text>
                    <Text as="p">
                      To take over care, please contact HealFaster support and
                      we'll move them to your account.
                    </Text>
                    <Link
                      url={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}
                    >
                      Email HealFaster support
                    </Link>
                  </BlockStack>
                </Banner>
              );
            }
            if (code === 'PATIENT_ALREADY_AT_CLINIC') {
              return (
                <Banner tone="warning">
                  <p>A patient with this email already exists at your clinic.</p>
                </Banner>
              );
            }
            return (
              <Banner tone="critical">
                <p>
                  {errBody?.message?.message ||
                    errBody?.message ||
                    'Failed to create patient. Please try again.'}
                </p>
              </Banner>
            );
          })()}

          <FormLayout>
            <Select
              label="Refer By"
              options={doctorOptions}
              value={referredByDoctorId}
              onChange={setReferredByDoctorId}
              requiredIndicator
            />

            <FormLayout.Group>
              <TextField
                label="First Name"
                value={firstName}
                onChange={setFirstName}
                autoComplete="given-name"
                requiredIndicator
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={setLastName}
                autoComplete="family-name"
                requiredIndicator
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                requiredIndicator
              />
              <TextField
                label="Telephone"
                type="tel"
                value={phone}
                onChange={setPhone}
                autoComplete="tel"
                requiredIndicator
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Age"
                type="number"
                value={age}
                onChange={setAge}
                autoComplete="off"
              />
              <Select
                label="Gender"
                options={GENDER_OPTIONS}
                value={gender}
                onChange={setGender}
                requiredIndicator
              />
            </FormLayout.Group>

            <TextField
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={setDateOfBirth}
              autoComplete="bday"
            />

            <TextField
              label="Address"
              value={streetAddress}
              onChange={setStreetAddress}
              autoComplete="street-address"
              requiredIndicator
            />

            <FormLayout.Group>
              <TextField
                label="City"
                value={city}
                onChange={setCity}
                autoComplete="address-level2"
                requiredIndicator
              />
              <Select
                label="State/Province"
                options={PROVINCE_OPTIONS}
                value={province}
                onChange={setProvince}
                requiredIndicator
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Zip Code"
                value={postalCode}
                onChange={setPostalCode}
                autoComplete="postal-code"
                requiredIndicator
              />
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={status}
                onChange={setStatus}
              />
            </FormLayout.Group>
          </FormLayout>

          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={mutation.isPending}
            disabled={!isValid}
          >
            Create Patient
          </Button>
        </BlockStack>
      </Card>
    </Page>
  );
}
